import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, MailCheck, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter v32 — Email verification landing.
 * On mount: auto-call /api/auth/verify-email with token from URL.
 * Provides "resend verification" if token expired/invalid.
 */

export default function VerifyEmail() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [state, setState] = useState({ phase: "loading", message: "Memverifikasi email…" });
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ phase: "error", message: "Token verifikasi tidak ditemukan di URL." });
      return;
    }
    (async () => {
      try {
        const r = await axios.post(`${API}/auth/verify-email`, { token });
        setState({ phase: "success", message: r.data.message || "Email berhasil diverifikasi. Silakan login.", email: r.data.email, alreadyVerified: r.data.already_verified });
      } catch (e) {
        setState({ phase: "error", message: e?.response?.data?.detail || "Verifikasi gagal." });
      }
    })();
  }, [token]);

  const resend = async () => {
    if (!resendEmail.trim()) return toast.error("Masukkan email Anda");
    setResending(true);
    try {
      const r = await axios.post(`${API}/auth/resend-verification`, { email: resendEmail.trim() });
      toast.success(r.data.message || "Link verifikasi dikirim ulang.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-stone-200 p-8 space-y-4" data-testid="verify-email-page">
        {state.phase === "loading" && (
          <div className="text-center space-y-3">
            <Loader2 className="h-12 w-12 text-terra-500 animate-spin mx-auto" />
            <p className="text-stone-700">{state.message}</p>
          </div>
        )}
        {state.phase === "success" && (
          <div className="space-y-3" data-testid="verify-email-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
            <h2 className="font-display text-2xl font-bold text-stone-900 text-center">{state.alreadyVerified ? "Sudah Diverifikasi" : "Berhasil Diverifikasi"}</h2>
            <p className="text-sm text-stone-700 text-center">{state.message}</p>
            {state.email && (
              <div className="bg-stone-50 border border-stone-200 p-3 text-xs font-mono text-center">{state.email}</div>
            )}
            <Link to="/login" className="block w-full bg-terra-500 hover:bg-terra-600 text-white py-2.5 text-center text-sm font-medium" data-testid="verify-email-go-login">
              Login Sekarang
            </Link>
          </div>
        )}
        {state.phase === "error" && (
          <div className="space-y-4" data-testid="verify-email-error">
            <XCircle className="h-12 w-12 text-red-600 mx-auto" />
            <h2 className="font-display text-2xl font-bold text-stone-900 text-center">Verifikasi Gagal</h2>
            <p className="text-sm text-stone-700 text-center">{state.message}</p>
            <div className="pt-3 border-t border-stone-100">
              <p className="text-xs text-stone-600 mb-2">Kirim ulang link verifikasi:</p>
              <div className="flex gap-2">
                <input type="email" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="Email Anda" className="flex-1 border border-stone-300 px-3 py-2 text-sm" data-testid="verify-email-resend-input" />
                <button onClick={resend} disabled={resending} className="bg-terra-500 hover:bg-terra-600 disabled:opacity-60 text-white px-3 py-2 text-sm" data-testid="verify-email-resend-btn">
                  <Mail className="h-3.5 w-3.5 inline" /> Kirim
                </button>
              </div>
            </div>
            <Link to="/login" className="block text-center text-xs text-stone-500 hover:underline">← Kembali ke Login</Link>
          </div>
        )}
      </div>
    </div>
  );
}
